import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Building2, CheckCircle } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [logo, setLogo] = useState(null);
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    fetchLogo();
    if (!token) {
      toast.error('Invalid reset link');
      navigate('/login');
    }
  }, [token, navigate]);

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/public/settings/company`);
      if (response.data.website_logo) {
        setLogo(response.data.website_logo);
      }
    } catch (error) {
      console.error('Failed to load logo');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });

      setSuccess(true);
      toast.success('Password reset successfully!');
      
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8 text-center">
            {logo ? (
              <div className="inline-flex items-center justify-center mb-4">
                <img src={logo} alt="Company Logo" className="h-24 w-auto object-contain" />
              </div>
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl mb-4 mx-auto">
                <Building2 className="w-8 h-8 text-white" />
              </div>
            )}
            
            <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-100 rounded-full mx-auto">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-slate-900">Password Reset Successful!</h1>
              <p className="text-slate-600">
                Your password has been reset successfully. You will be redirected to the login page in a moment.
              </p>
            </div>
            
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          <div className="text-center space-y-2">
            {logo ? (
              <div className="inline-flex items-center justify-center mb-4">
                <img src={logo} alt="Company Logo" className="h-24 w-auto object-contain" />
              </div>
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl mb-4">
                <Building2 className="w-8 h-8 text-white" />
              </div>
            )}
            <h1 className="text-3xl font-bold text-slate-900">Reset Password</h1>
            <p className="text-slate-600">Enter your new password</p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">
                New Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 h-12 border-slate-200 focus:border-emerald-500"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">
                Confirm Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-11 h-12 border-slate-200 focus:border-emerald-500"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg shadow-lg shadow-emerald-500/30"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full text-sm text-slate-600 hover:text-emerald-600"
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
